import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Overlay geometry.
 *
 * Everything the API sends is a fraction of the page (0–1), so turning it into
 * CSS is one multiplication. There is no percentage string parsing, no `%%`
 * escaping, and no separate shape per overlay kind — which is what the legacy
 * `sig_overlays` / `checkbox_overlays` / `reg_overlays` payloads each needed.
 *
 * Rounded to four decimal places of a percent: that is a fraction of a pixel on
 * any screen, and it keeps binary floating-point noise out of the CSS —
 * `0.029 * 100` is `2.9000000000000004`, which is both ugly and needlessly
 * engine-dependent.
 */
function pct(value: number): string {
  return `${Number((value * 100).toFixed(4))}%`
}

export function overlayStyle(box: { x: number; y: number; w: number; h: number }) {
  return {
    left: pct(box.x),
    top: pct(box.y),
    width: pct(box.w),
    height: pct(box.h),
  } as const
}

export function pointStyle(point: { x: number; y: number }) {
  return { left: pct(point.x), top: pct(point.y) } as const
}

// ── Formatting ──────────────────────────────────────────────────────

export function formatDate(value: string | Date | null | undefined, pattern = 'dd/MM/yyyy') {
  if (!value) return '—'
  const date = typeof value === 'string' ? parseISO(value) : value
  return Number.isNaN(date.getTime()) ? '—' : format(date, pattern)
}

export function formatDateTime(value: string | Date | null | undefined) {
  return formatDate(value, 'dd/MM/yyyy HH:mm')
}

export function formatRelative(value: string | Date | null | undefined) {
  if (!value) return '—'
  const date = typeof value === 'string' ? parseISO(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  return `${formatDistanceToNowStrict(date)} ago`
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds == null) return '—'
  const whole = Math.round(seconds)
  if (whole < 60) return `${whole}s`
  const minutes = Math.floor(whole / 60)
  return `${minutes}m ${String(whole % 60).padStart(2, '0')}s`
}

export function formatBytes(bytes: number | null | undefined) {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

export function formatMoney(amount: number | null | undefined, currency: string | null) {
  if (amount == null) return '—'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'AED',
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ''}`.trim()
  }
}

export function formatPercent(value: number | null | undefined, digits = 0) {
  if (value == null) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

/** How a figure moved between two periods, for a tile's corner badge. */
export interface Change {
  /** "+3", "−2", "12s faster": what moved, in figures and words. */
  label: string
  direction: 'up' | 'down'
  /** Whether that is the good direction for this measure. */
  better: boolean
}

/**
 * How a count moved since the previous period — or null when there is
 * nothing to say, because a figure is missing or nothing moved. Both figures
 * come from the server (DashboardStats carries yesterday's beside today's);
 * a delta the browser worked out from one number would be invented.
 */
export function countChange(
  now: number | null | undefined,
  before: number | null | undefined,
): Change | null {
  if (now == null || before == null || now === before) return null
  const diff = now - before
  return {
    label: `${diff > 0 ? '+' : '−'}${Math.abs(diff)}`,
    direction: diff > 0 ? 'up' : 'down',
    better: diff > 0,
  }
}

/** The same for a duration, where less is better. Compared to the second. */
export function durationChange(
  now: number | null | undefined,
  before: number | null | undefined,
): Change | null {
  if (now == null || before == null) return null
  const diff = Math.round(now) - Math.round(before)
  if (diff === 0) return null
  return {
    label: `${formatDuration(Math.abs(diff))} ${diff < 0 ? 'faster' : 'slower'}`,
    direction: diff > 0 ? 'up' : 'down',
    better: diff < 0,
  }
}

// ── Small helpers ───────────────────────────────────────────────────

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Confidence bands, used consistently everywhere a score is shown.
 *
 * Colour is never the only carrier of meaning (docs/08-uiux.md §10) — the
 * number is always displayed alongside.
 */
export function confidenceTone(confidence: number | null | undefined) {
  if (confidence == null) return 'neutral' as const
  if (confidence >= 0.85) return 'good' as const
  if (confidence >= 0.7) return 'fair' as const
  return 'poor' as const
}

/**
 * Hand the browser a file that arrived as a response body, for a download
 * that has to be a POST - one that issues something, so a plain link would
 * re-issue it on every prefetch or reload.
 */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  // Revoked late: Chromium reads the blob after the click returns.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export function downloadUrl(url: string) {
  // The API sets Content-Disposition; ?download=true flips it to attachment.
  return url.includes('?') ? `${url}&download=true` : `${url}?download=true`
}

export function uuid() {
  // randomUUID exists only in a secure context (HTTPS or localhost), so a phone
  // that opens the dev server by its LAN address has none, and every toast
  // threw. getRandomValues has no such restriction.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // RFC 4122 variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
